import React, { Component } from "react";
import { Formik, Form, FieldArray, Field, getIn } from "formik";
import * as yup from "yup";
import { Input, Button, Alert } from "@maxmilhas/hangar-components-atomic/public";
import { css } from "emotion";
import moment from "moment";
import PubSub from "pubsub-js";
import AirportAutoComplete from "./AirportAutoComplete";
import PassengersDropdown from "./PassengersDropdown";
import StopsDropdown from "./StopsDropdown";
import OptionsRow from "./OptionsRow";
import {
    banner, ilustration, marginBottom, centerBlockMessage, well, headerTitle,
    headerSubtitle, headerLead, title, wrapper, flexWrapper, flexWrap
} from "./style";
import { getUser } from "../../vanilla/userUtils";
import { uuidv4 } from "../../shared/utils";
import { createAirBridge } from "../../shared/requests-api";
import ErrorFocus from "./ErrorFocus";

import "../../shared/pubsub-triggers";

const fromSchema = yup.string().required("Escolha a origem da sua viagem.");
const toSchema = yup.string().required("Escolha o destino da sua viagem.");

const validationSchema = yup.object().shape({
    from: fromSchema
        .test("from-validation", "Selecione um local diferente do destino.", async function (value) {
            if (await fromSchema.isValid(value)) {
                return  this.parent.to !== value;
            }
            return true;
        }),
    to: toSchema
        .test("to-validation", "Selecione um local diferente da origem.", async function (value) {
            if (await toSchema.isValid(value)) {
                return this.parent.from !== value;
            }
            return true;
        }),
    adults: yup.number(),
    infants: yup.number()
        .max(yup.ref('adults'), "O número máximo de bebês é de 1 para cada adulto."),
    alerts: yup.array()
        .of(
            yup.object().shape({
                outboundDate: yup.date().typeError("Você precisa informar uma data."),
                inboundDate: yup.date().typeError("Você precisa informar uma data."),
                targetPrice: yup.number().positive("Digite um valor maior que zero.")
            })
        ).min(1).max(5),
    user: yup.object().shape({
        email: yup.string()
            .email("Você precisa informar um e-mail válido.")
            .required("Você precisa informar um e-mail para cadastro.")
    })
});

let formInitialValues = {
    from: '',
    to: '',
    user: {
        name: '',
        email: '',
        idMM: '',
        token: ''
    },
    tripType: 'RT',
    stops: [],
    adults: 1,
    children: 0,
    infants: 0,
    cabin: 'EC',
    alerts: [
        {
            id: uuidv4(),
            outboundDate: null,
            outboundTime: [],
            inboundDate: null,
            inboundTime: [],
            targetPrice: 0
        },
        {
            id: uuidv4(),
            outboundDate: null,
            outboundTime: [],
            inboundDate: null,
            inboundTime: [],
            targetPrice: 0
        }
    ]
};

class MaxAirBridge extends Component {
    constructor(props) {
        super(props);

        this.state = { statusSuccess: null };

        this.onSubmit = this.onSubmit.bind(this);
        this.setAirportsValue = this.setAirportsValue.bind(this);
        this.setPassengersAndCabin = this.setPassengersAndCabin.bind(this);

        const user = getUser();

        if (user) {
            formInitialValues.user = {
                name: user.name,
                email: user.email,
                idMM: user.id,
                token: '',
            };
        }
    }

    componentDidMount() {
        window.scrollTo(0, 0);
    }

    setAirportsValue(data, setFieldValue) {
        setFieldValue("from", data.outboundIata);
        setFieldValue("to", data.inboundIata);
    }

    setPassengersAndCabin(data, setFieldValue) {
        setFieldValue("adults", data.adults);
        setFieldValue("children", data.children);
        setFieldValue("infants", data.infants);
        setFieldValue("cabin", data.cabin)
    }

    onSubmit(values, { setSubmitting, resetForm }) {
        const alerts = values.alerts.map(alert => {
            const { id, outboundDate, inboundDate, ...rest } = alert;
            
            return {
                ...rest,
                outboundDate: moment(outboundDate).format("YYYY-MM-DD"),
                inboundDate: moment(inboundDate).format("YYYY-MM-DD"),
            }
        });

        const user = { ...values.user };

        Object.keys(user).forEach((key) => (user[key] === "") && delete user[key]);

        const isMobile = window.isMobileDevice();
        const origin = isMobile ? "MM-SITE-MOBILE" : "MM-SITE-DESKTOP";

        createAirBridge({ ...values, alerts, user, origin })
            .then(response => {
                if (response.status === 201) {
                    this.setState({ statusSuccess: true });
                    
                    setSubmitting(false);

                    if (user.idMM) {
                        this.sendGAEvents({
                            gaEventAction: "Confirmação de cadastro",
                            gaEventLabel: "Botão cadastrar - logado"
                        });
                    } else {
                        this.sendGAEvents({
                            gaEventAction: "Confirmação de cadastro",
                            gaEventLabel: "Botão cadastrar - deslogado"
                        });
                    }

                    setTimeout(() => window.location.reload(true), 2700);
                }
            }).catch(error => {
                this.setState({ statusSuccess: false });
                setSubmitting(false);
            });
    }

    sendGAEvents(data) {
        PubSub.publish("dataLayer", {
            event: "gaEvent",
			gaEventCategory: "Ponte aérea max",
            ...data
        });
    }

    render() {
        return (
            <Formik
                initialValues={formInitialValues}
                onSubmit={this.onSubmit}
                validationSchema={validationSchema}
                render={({ values, errors, touched, setFieldValue, isSubmitting }) => {
                    return (
                        <Form style={{ backgroundColor: "#FFF" }}>
                            <section className={css`${banner}`}>
                                <article className="container">
                                    <header className={css`${wrapper}; ${ilustration}`}>
                                        <h1 className={css`${headerTitle}`}>
                                            Ponte Aérea Max
                                            <span className={css`${headerSubtitle}`}>
                                                Você sabe pra onde ir.
                                                <br />
                                                A gente te ajuda a chegar lá.
                                            </span>
                                        </h1>
                                        <p className={css`${headerLead}`}>
                                            É só escolher 5 datas para o destino que você viaja sempre.
                                            <span className="hidden-xs">
                                                {" "}Seja a trabalho ou para matar a saudade, a única coisa que você precisa fazer é viajar.
                                            </span>
                                        </p>
                                    </header>
                                </article>
                            </section>
                            <section className="container">
                                <article className={css`${wrapper}`}>
                                    <h4 className={css`${title}`}>Para onde você viaja sempre?</h4>
                                    <section className="row">
                                        <div className="col-xs-16 col-md-8">
                                            <AirportAutoComplete
                                                getValue={(data => this.setAirportsValue(data, setFieldValue))}
                                                hasError={{
                                                    from: touched.from && errors.from,
                                                    to: touched.to && errors.to
                                                }}
                                            />
                                        </div>
                                        <div className="col-xs-16 col-sm-8 col-md-4">
                                            <div className={css`${marginBottom}`}>
                                                <PassengersDropdown
                                                    getValue={(data => this.setPassengersAndCabin(data, setFieldValue))}
                                                    hasError={{
                                                        infants: touched.infants && errors.infants
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-xs-16 col-sm-8 col-md-4">
                                            <div className={css`${marginBottom}`}>
                                                <StopsDropdown getValue={(stops => setFieldValue("stops", stops))} />
                                            </div>
                                        </div>
                                    </section>
                                </article>
                                <article className={css`${wrapper}`}>
                                    <h4 className={css`${title}`}>Em quais datas você planeja viajar?</h4>
                                    <FieldArray
                                        name="alerts"
                                        render={({ remove, push }) => (
                                            <>
                                                {values.alerts && values.alerts.map((alert, index) => (
                                                    <OptionsRow
                                                        key={alert.id}
                                                        index={index}
                                                        onUpdate={(field, value) => setFieldValue(`alerts.${index}.${field}`, value)}
                                                        onRemove={() => {
                                                            remove(index);

                                                            this.sendGAEvents({
                                                                gaEventAction: "Para onde você viaja sempre",
                                                                gaEventLabel: "Excluir uma data"
                                                            });
                                                        }}
                                                        canDelete={values.alerts.length > 1}
                                                        hasError={{
                                                            outboundDate: getIn(touched, `alerts.${index}.outboundDate`) && getIn(errors, `alerts.${index}.outboundDate`),
                                                            inboundDate: getIn(touched, `alerts.${index}.inboundDate`) && getIn(errors, `alerts.${index}.inboundDate`),
                                                            targetPrice: getIn(touched, `alerts.${index}.targetPrice`) && getIn(errors, `alerts.${index}.targetPrice`)
                                                        }}
                                                    />
                                                ))}
                                                <div className="row">
                                                    <div className="col-xs-16 col-md-4">
                                                        <Button size="small" disabled={values.alerts.length === 5} onClick={() => {
                                                            push({
                                                                id: uuidv4(),
                                                                outboundDate: null,
                                                                outboundTime: [],
                                                                inboundDate: null,
                                                                inboundTime: [],
                                                                targetPrice: 0
                                                            });
                                                            
                                                            this.sendGAEvents({
                                                                gaEventAction: "Para onde você viaja sempre",
                                                                gaEventLabel: "Adicionar data"
                                                            });
                                                        }}>Adicionar uma nova data</Button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    />
                                </article>
                                <article className={css`${wrapper}`}>
                                    <fieldset className={css`${well}`}>
                                        <div className="row">
                                            <div className="col-xs-16 col-md-6">
                                                <h3 className={css`${title}`}>
                                                    {formInitialValues.user.idMM === ''
                                                        ? "Deixe seu e-mail e receba os melhores preços para sua viagem."
                                                        : "Cadastre sua Ponte Aérea Max e receba seus voos por e-mail."}
                                                </h3>
                                            </div>
                                            <div className="col-xs-16 col-md-offset-1 col-md-9">
                                                <div className={css`${flexWrapper}; ${flexWrap}`}>
                                                    {formInitialValues.user.idMM === '' && (
                                                        <div className="flex-item" data-tooltip={touched.user && errors.user && touched.user.email && errors.user.email} data-flow="down">
                                                            <Field
                                                                name="user.email"
                                                                render={({ field }) => (
                                                                    <Input
                                                                        {...field}
                                                                        shadow={true}
                                                                        placeholder="Digite aqui o seu e-mail..."
                                                                        hasError={
                                                                            touched.user && errors.user &&
                                                                            touched.user.email && errors.user.email
                                                                        }
                                                                        data-action="maxairbridge-form-email"
                                                                        aria-required="true"
                                                                        aria-invalid={errors.user && errors.user.email ? true : false}
                                                                        aria-label={errors.user && errors.user.email ? errors.user.email : "Digite aqui o seu e-mail"}
                                                                    />
                                                                )}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="flex-item">
                                                        <Button disabled={isSubmitting} type="submit">
                                                            {/*isSubmitting && <i className="loader loader-fetching"></i>*/}
                                                            Cadastrar
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </fieldset>
                                    {this.state.statusSuccess !== null && (
                                        <Alert icon={this.state.statusSuccess ? "icon-check" : "icon-exclamation"} type={this.state.statusSuccess ? "success" : "danger"}>
                                            {this.state.statusSuccess
                                                ? "Sua Ponte Aérea Max foi cadastrada com sucesso! Agora é com a gente. Vamos encontrar as melhores opções de viagem para suas datas e enviá-las para o seu e-mail."
                                                : "Opa! Algo deu errado. Que tal tentar de novo?"
                                            }
                                        </Alert>
                                    )}
                                    <div className={css`${centerBlockMessage}`}>
                                        <p><strong>Não enviamos spam. Você vai receber somente as viagens cadastradas.</strong></p>
                                    </div>
                                </article>
                            </section>
                            <ErrorFocus />
                        </Form>
                    );
                }}
            />
        )
    }
}

export default MaxAirBridge;